import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { ShopHeader } from "@/components/ShopHeader";
import { ShopFooter } from "@/components/ShopFooter";
import { fmtCurrencyPrecise } from "@/lib/format";
import { Receipt, DownloadSimple, Package, Storefront, MapPin, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUS = {
  new: { key: "statusNew", cls: "bg-blue-500/10 text-blue-600" },
  priced: { key: "statusPriced", cls: "bg-amber-500/10 text-amber-600" },
  invoiced: { key: "statusInvoiced", cls: "bg-violet-500/10 text-violet-600" },
  sent: { key: "statusSent", cls: "bg-emerald-500/10 text-emerald-600" },
};

export default function ClientPortalPage() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/me/orders`).then((r) => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${API}/me/succursales`).then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  const setBranch = (i, patch) => setBranches((bs) => bs.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const saveBranch = async (b) => {
    setSavingId(b.id);
    try {
      await axios.put(`${API}/me/succursales/${b.id}`, {
        succursale: b.succursale, address: b.address, city: b.city, province: b.province,
        postal_code: b.postal_code, phone: b.phone, fax: b.fax, general_contact: b.general_contact,
      });
      toast.success(t.saved);
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); }
    finally { setSavingId(null); }
  };

  const download = (token) => { if (token) window.open(`${BACKEND}/api/shop/invoices/${token}`, "_blank"); };

  return (
    <div className="min-h-screen bg-[#f4f7f9]">
      <ShopHeader />
      <main className="mx-auto max-w-[1000px] px-4 py-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{t.clientPortal}</div>
            <h1 className="mt-1 font-display text-3xl font-black tracking-tighter text-[#2e3a47]">{t.welcome}, {user?.name || user?.email}</h1>
          </div>
          <button data-testid="portal-shop-link" onClick={() => navigate("/shop")}
            className="inline-flex items-center gap-2 border border-border bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#2e3a47] hover:border-primary hover:text-primary transition-colors duration-200">
            <Storefront size={16} weight="bold" /> {t.catalog}
          </button>
        </div>

        <h2 className="mb-3 border-b-2 border-primary pb-2 font-display text-xl font-bold uppercase tracking-wide text-[#2e3a47]">{t.myOrders}</h2>

        {loading && <div className="py-16 text-center text-muted-foreground">{t.loading}</div>}
        {!loading && orders.length === 0 && (
          <div className="border border-border bg-white p-12 text-center">
            <Package size={40} className="mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t.noOrders}</p>
            <button onClick={() => navigate("/shop")} className="mt-4 bg-primary px-4 py-2 text-xs font-bold uppercase tracking-wider text-white">{t.continueShopping}</button>
          </div>
        )}

        <div className="space-y-4" data-testid="portal-orders">
          {!loading && orders.map((o) => {
            const s = STATUS[o.status] || STATUS.new;
            return (
              <div key={o.id} data-testid={`portal-order-${o.id}`} className="border border-border bg-white p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-block px-2 py-0.5 text-xs font-bold ${s.cls}`}>{t[s.key]}</span>
                      {o.invoice_number && <span className="font-mono text-xs text-muted-foreground">{o.invoice_number}</span>}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">{o.created_at ? new Date(o.created_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA") : ""} · {o.items?.length || 0} {t.items}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    {o.grand_total != null && <div className="text-right"><div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t.grandTotal}</div><div className="font-display text-xl font-black text-[#2e3a47] tabular-nums">{fmtCurrencyPrecise(o.grand_total, lang)}</div></div>}
                    {o.invoice_token ? (
                      <button data-testid={`portal-invoice-${o.id}`} onClick={() => download(o.invoice_token)}
                        className="inline-flex items-center gap-2 bg-primary px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5">
                        <DownloadSimple size={16} weight="bold" /> {t.viewInvoice}
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-2 text-xs text-muted-foreground"><Receipt size={16} /> {t.pending}</span>
                    )}
                  </div>
                </div>
                <div className="mt-3 border-t border-border pt-3">
                  {(o.items || []).map((it, i) => (
                    <div key={i} className="flex items-center justify-between py-1 text-sm">
                      <span className="text-[#2e3a47]"><span className="font-mono text-xs text-primary">{it.product_code}</span> · {it.name}</span>
                      <span className="text-muted-foreground">×{it.qty}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {branches.length > 0 && (
          <div className="mt-10" data-testid="portal-branches">
            <h2 className="mb-3 flex items-center gap-2 border-b-2 border-primary pb-2 font-display text-xl font-bold uppercase tracking-wide text-[#2e3a47]"><MapPin size={20} weight="bold" className="text-primary" /> {t.myBranches}</h2>
            <div className="space-y-4">
              {branches.map((b, i) => (
                <div key={b.id} data-testid={`branch-${b.id}`} className="border border-border bg-white p-5">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">{b.entreprise}</div>
                      <div className="font-display text-lg font-black text-[#2e3a47]">{b.succursale}</div>
                    </div>
                    <button data-testid={`branch-save-${b.id}`} onClick={() => saveBranch(b)} disabled={savingId === b.id}
                      className="inline-flex items-center gap-2 bg-primary px-4 py-2 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
                      <FloppyDisk size={15} weight="bold" /> {t.save}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <input data-testid={`branch-address-${b.id}`} className="border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary sm:col-span-2 lg:col-span-1" placeholder={t.shippingAddress} value={b.address || ""} onChange={(e) => setBranch(i, { address: e.target.value })} />
                    <input className="border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" placeholder={t.shipCity} value={b.city || ""} onChange={(e) => setBranch(i, { city: e.target.value })} />
                    <input className="border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" placeholder={t.shipProvince} value={b.province || ""} onChange={(e) => setBranch(i, { province: e.target.value })} />
                    <input className="border border-border bg-background px-3 py-2 font-mono text-sm outline-none focus:border-primary" placeholder={t.shipPostal} value={b.postal_code || ""} onChange={(e) => setBranch(i, { postal_code: e.target.value })} />
                    <input className="border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" placeholder={t.phone} value={b.phone || ""} onChange={(e) => setBranch(i, { phone: e.target.value })} />
                    <input data-testid={`branch-fax-${b.id}`} className="border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" placeholder={t.fax} value={b.fax || ""} onChange={(e) => setBranch(i, { fax: e.target.value })} />
                    <input className="border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary sm:col-span-2 lg:col-span-2" placeholder={t.generalContact} value={b.general_contact || ""} onChange={(e) => setBranch(i, { general_contact: e.target.value })} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      <ShopFooter />
    </div>
  );
}
